// panelGeometry.ts — Shared panel outline geometry for 2D, 3D, calcs, and DXF export.
//
// Computes the actual panel outline vertices from corner heights + top angles.
// Supports rectangle, trapezoid, pentagon (gable), hexagon (clipped gable), and valley shapes.
//
// Angle convention:
//   0° = horizontal at corner height
//   Positive = slopes UP inward (gable / peak)
//   Negative = slopes DOWN inward (valley / notch)
//   "auto" = direct line to other corner's height
//
// Height convention:
//   mp.height = max/cap height (overall panel height limit)
//   heightL = left edge height (default: height)
//   heightR = right edge height (default: heightL)

import * as THREE from 'three';

// ── Types ────────────────────────────────────────────────────────────────────

export type AngleSpec = number | 'auto';

export interface TopAngleParams {
  width:    number;   // panel width inches
  height:   number;   // max/cap height inches
  heightL?: number;   // left edge height (default: height)
  heightR?: number;   // right edge height (default: heightL ?? height)
  angleL:   AngleSpec; // left top corner angle (degrees or 'auto')
  angleR:   AngleSpec; // right top corner angle (degrees or 'auto')
  radiusL:  number;   // left arc fillet radius (0 = sharp)
  radiusR:  number;   // right arc fillet radius (0 = sharp)
}

export type ShapeType = 'rect' | 'trapezoid' | 'pentagon' | 'hexagon';

export interface Vertex { x: number; y: number; }

export interface PanelOutline {
  /** Closed polygon vertices (CCW winding, bottom-left origin) */
  vertices:  Vertex[];
  /** Shape classification for UI/debugging */
  shapeType: ShapeType;
  /** Resolved left edge height */
  hL: number;
  /** Resolved right edge height */
  hR: number;
  /** Resolved left angle in degrees */
  resolvedAngleL: number;
  /** Resolved right angle in degrees */
  resolvedAngleR: number;
  /** Peak or ridge vertex (for pentagon/hexagon), null for rect/trap */
  peak: Vertex | null;
  /** Left clip X where slope meets cap (hexagon only) */
  clipXL: number | null;
  /** Right clip X where slope meets cap (hexagon only) */
  clipXR: number | null;
}

// ── Constants ────────────────────────────────────────────────────────────────

const DEG = Math.PI / 180;
const TOLERANCE = 0.001; // inches — merge vertices closer than this

// ── Pitch/angle conversion ───────────────────────────────────────────────────

/** Parse pitch string "rise/run" (e.g., "4/12") to degrees. Returns NaN if invalid. */
export function pitchToDeg(pitch: string): number {
  const m = pitch.trim().match(/^(-?\d+(?:\.\d+)?)\s*[/]\s*(\d+(?:\.\d+)?)$/);
  if (!m) return NaN;
  const rise = parseFloat(m[1]);
  const run  = parseFloat(m[2]);
  if (run === 0) return NaN;
  return Math.atan(rise / run) / DEG;
}

/** Convert degrees to nearest standard pitch "rise/12". Returns e.g. "4/12". */
export function degToPitch(deg: number): string {
  const rise = Math.tan(deg * DEG) * 12;
  return `${Math.round(rise * 100) / 100}/12`;
}

/** Parse angle input — accepts degrees ("30"), pitch ("4/12"), or "auto". */
export function parseAngleInput(input: string): AngleSpec {
  const s = input.trim().toLowerCase();
  if (s === 'auto' || s === '') return 'auto';
  // Try pitch notation first
  const pd = pitchToDeg(s);
  if (!isNaN(pd)) return pd;
  // Try plain number (degrees)
  const n = parseFloat(s);
  if (!isNaN(n)) return n;
  return 'auto';
}

/** Format angle for display: degrees with pitch equivalent. */
export function formatAngle(angle: AngleSpec): string {
  if (angle === 'auto') return 'Auto';
  const d = angle;
  const pitch = degToPitch(d);
  return `${d.toFixed(1)}° (${pitch})`;
}

// ── Auto angle computation ───────────────────────────────────────────────────

/** Compute the "auto" angle for a corner — the slope needed to reach the other corner directly. */
export function autoAngle(hThis: number, hOther: number, width: number): number {
  if (width <= 0) return 0;
  return Math.atan2(hOther - hThis, width) / DEG;
}

/** Minimum allowed angle for a corner — prevents slopes from diverging (never reaching each other). */
export function minAngle(hThis: number, hOther: number, width: number): number {
  return autoAngle(hThis, hOther, width);
}

// ── Outline computation ──────────────────────────────────────────────────────

/** Resolve effective heights, defaulting heightL → height, heightR → heightL. */
function resolveHeights(p: TopAngleParams): { hL: number; hR: number } {
  const hL = p.heightL ?? p.height;
  const hR = p.heightR ?? hL;
  return { hL, hR };
}

// ── Corner fillet arc ────────────────────────────────────────────────────────

const FILLET_SEGMENTS = 8;

/**
 * Compute fillet arc vertices to replace a sharp corner.
 * Returns array of arc points (or single corner vertex if radius ≤ 0).
 */
function computeFilletArc(prev: Vertex, corner: Vertex, next: Vertex, radius: number): Vertex[] {
  if (radius <= 0) return [corner];

  // Vectors from corner toward adjacent vertices
  const d1x = prev.x - corner.x, d1y = prev.y - corner.y;
  const d2x = next.x - corner.x, d2y = next.y - corner.y;
  const len1 = Math.sqrt(d1x * d1x + d1y * d1y);
  const len2 = Math.sqrt(d2x * d2x + d2y * d2y);
  if (len1 < TOLERANCE || len2 < TOLERANCE) return [corner];

  // Unit vectors
  const u1x = d1x / len1, u1y = d1y / len1;
  const u2x = d2x / len2, u2y = d2y / len2;

  // Half-angle between the two edges
  const cosA = Math.max(-1, Math.min(1, u1x * u2x + u1y * u2y));
  const halfA = Math.acos(cosA) / 2;
  if (halfA < 1e-6 || halfA > Math.PI - 1e-6) return [corner];

  // Distance from corner to tangent point
  let tanDist = radius / Math.tan(halfA);
  // Clamp to 90% of the shorter edge so the fillet fits without overlapping
  const maxDist = Math.min(len1, len2) * 0.9;
  if (tanDist > maxDist) {
    tanDist = maxDist;
    radius = tanDist * Math.tan(halfA);
  }

  // Tangent points on each edge
  const t1: Vertex = { x: corner.x + u1x * tanDist, y: corner.y + u1y * tanDist };
  const t2: Vertex = { x: corner.x + u2x * tanDist, y: corner.y + u2y * tanDist };

  // Arc center along angle bisector
  const bx = u1x + u2x, by = u1y + u2y;
  const bLen = Math.sqrt(bx * bx + by * by);
  if (bLen < 1e-6) return [corner];

  const centerDist = radius / Math.sin(halfA);
  const cx = corner.x + (bx / bLen) * centerDist;
  const cy = corner.y + (by / bLen) * centerDist;

  // Arc from t1 → t2
  const a1 = Math.atan2(t1.y - cy, t1.x - cx);
  const a2 = Math.atan2(t2.y - cy, t2.x - cx);

  // Determine sweep direction — cross product tells us interior side
  const cross = d1x * d2y - d1y * d2x;
  let sweep = a2 - a1;
  if (cross > 0) {
    if (sweep > 0) sweep -= 2 * Math.PI;
  } else {
    if (sweep < 0) sweep += 2 * Math.PI;
  }

  const pts: Vertex[] = [];
  for (let i = 0; i <= FILLET_SEGMENTS; i++) {
    const t = i / FILLET_SEGMENTS;
    const a = a1 + sweep * t;
    pts.push({ x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) });
  }
  return pts;
}

/**
 * Apply fillet arcs to TL and TR corners of a vertex array.
 * TL is always the last vertex; TR is always at index 2.
 * Fillets the last vertex first (TL) so TR index stays at 2.
 */
function applyFillets(verts: Vertex[], radiusL: number, radiusR: number): Vertex[] {
  let result = verts;
  const n = result.length;

  // Fillet TL (last index) first — preserves index 2 for TR
  if (radiusL > 0 && n >= 3) {
    const idx = result.length - 1;
    const prev = result[(idx - 1 + result.length) % result.length];
    const corner = result[idx];
    const next = result[0]; // wraps to BL
    const arcPts = computeFilletArc(prev, corner, next, radiusL);
    if (arcPts.length > 1) {
      result = [...result.slice(0, idx), ...arcPts];
    }
  }

  // Fillet TR (index 2)
  if (radiusR > 0 && result.length >= 4) {
    const prev = result[1]; // BR
    const corner = result[2]; // TR
    const next = result[3]; // peak or clipR or TL-arc start
    const arcPts = computeFilletArc(prev, corner, next, radiusR);
    if (arcPts.length > 1) {
      result = [...result.slice(0, 2), ...arcPts, ...result.slice(3)];
    }
  }

  return result;
}

/**
 * Compute the panel outline from top-angle parameters.
 *
 * The outline is a closed polygon with vertices in CCW winding order,
 * bottom-left origin (0,0) = panel bottom-left corner.
 *
 * Shape determination:
 *   - Both angles produce a flat top at same height → rectangle
 *   - Both auto, different heights → trapezoid (straight slope)
 *   - Angles create a peak ≤ height → pentagon (gable)
 *   - Peak > height → hexagon (clipped gable with flat top)
 */
export function computePanelOutline(p: TopAngleParams): PanelOutline {
  const { hL, hR } = resolveHeights(p);
  const W = p.width;
  const cap = p.height;

  // When both are auto → use autoAngle (direct slope TL→TR).
  // When one is auto and the other manual → auto resolves to 0° (flat from corner)
  //   so the manual angle creates a visible slope/peak.
  const bothAuto = p.angleL === 'auto' && p.angleR === 'auto';
  const angL = p.angleL === 'auto'
    ? (bothAuto ? autoAngle(hL, hR, W) : 0)
    : p.angleL;
  const angR = p.angleR === 'auto'
    ? (bothAuto ? autoAngle(hR, hL, W) : 0)
    : p.angleR;

  const tanL = Math.tan(angL * DEG);
  const tanR = Math.tan(angR * DEG);

  // Bottom-left, bottom-right always present
  const BL: Vertex = { x: 0,  y: 0 };
  const BR: Vertex = { x: W,  y: 0 };
  const TL: Vertex = { x: 0,  y: hL };
  const TR: Vertex = { x: W,  y: hR };

  // Build the raw outline (sharp corners), then apply fillets at the end.
  let outline: PanelOutline;

  // Determine if slopes are effectively flat (near-zero angle with same heights)
  const isFlat = Math.abs(angL) < TOLERANCE && Math.abs(angR) < TOLERANCE && Math.abs(hL - hR) < TOLERANCE;

  if (isFlat) {
    outline = {
      vertices: [BL, BR, TR, TL],
      shapeType: 'rect',
      hL, hR,
      resolvedAngleL: angL,
      resolvedAngleR: angR,
      peak: null, clipXL: null, clipXR: null,
    };
  } else {
    // Check if both are effectively "auto" (straight slope from TL to TR)
    const autoL = autoAngle(hL, hR, W);
    const autoR = autoAngle(hR, hL, W);
    const isAutoL = Math.abs(angL - autoL) < TOLERANCE;
    const isAutoR = Math.abs(angR - autoR) < TOLERANCE;

    if (isAutoL && isAutoR) {
      outline = {
        vertices: [BL, BR, TR, TL],
        shapeType: hL === hR ? 'rect' : 'trapezoid',
        hL, hR,
        resolvedAngleL: angL,
        resolvedAngleR: angR,
        peak: null, clipXL: null, clipXR: null,
      };
    } else {
      // Compute intersection point of two slopes (potential peak/valley)
      const denom = tanL + tanR;

      if (Math.abs(denom) < TOLERANCE * TOLERANCE) {
        outline = {
          vertices: [BL, BR, TR, TL],
          shapeType: 'trapezoid',
          hL, hR,
          resolvedAngleL: angL,
          resolvedAngleR: angR,
          peak: null, clipXL: null, clipXR: null,
        };
      } else {
        const xPeak = (hR - hL + W * tanR) / denom;

        if (xPeak < -TOLERANCE || xPeak > W + TOLERANCE) {
          outline = {
            vertices: [BL, BR, TR, TL],
            shapeType: 'trapezoid',
            hL, hR,
            resolvedAngleL: angL,
            resolvedAngleR: angR,
            peak: null, clipXL: null, clipXR: null,
          };
        } else {
          const peakX = Math.max(0, Math.min(W, xPeak));
          const peakY = hL + peakX * tanL;
          const peak: Vertex = { x: peakX, y: peakY };

          if (peakY <= cap + TOLERANCE) {
            outline = {
              vertices: [BL, BR, TR, peak, TL],
              shapeType: 'pentagon',
              hL, hR,
              resolvedAngleL: angL,
              resolvedAngleR: angR,
              peak, clipXL: null, clipXR: null,
            };
          } else {
            // Hexagon — peak exceeds cap, clip at cap height
            let clipXL: number;
            let clipXR: number;

            if (Math.abs(tanL) < TOLERANCE * TOLERANCE) {
              clipXL = 0;
            } else {
              clipXL = (cap - hL) / tanL;
            }

            if (Math.abs(tanR) < TOLERANCE * TOLERANCE) {
              clipXR = W;
            } else {
              clipXR = W - (cap - hR) / tanR;
            }

            clipXL = Math.max(0, Math.min(W, clipXL));
            clipXR = Math.max(clipXL, Math.min(W, clipXR));

            const verts: Vertex[] = [BL, BR, TR];
            if (clipXR < W - TOLERANCE) {
              verts.push({ x: clipXR, y: cap });
            }
            if (clipXL < clipXR - TOLERANCE) {
              verts.push({ x: clipXL, y: cap });
            }
            verts.push(TL);

            outline = {
              vertices: verts,
              shapeType: 'hexagon',
              hL, hR,
              resolvedAngleL: angL,
              resolvedAngleR: angR,
              peak,
              clipXL, clipXR,
            };
          }
        }
      }
    }
  }

  // Apply corner fillets to TL and TR
  const rL = p.radiusL ?? 0;
  const rR = p.radiusR ?? 0;
  if (rL > 0 || rR > 0) {
    outline.vertices = applyFillets(outline.vertices, rL, rR);
  }

  return outline;
}

// ── Area & centroid (Shoelace formula) ───────────────────────────────────────

/** Signed area of a polygon (CCW = positive). */
function signedArea(verts: Vertex[]): number {
  let a = 0;
  const n = verts.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a += verts[i].x * verts[j].y;
    a -= verts[j].x * verts[i].y;
  }
  return a / 2;
}

/** Compute area of the panel outline polygon (square inches). */
export function computePanelArea(outline: PanelOutline): number {
  return Math.abs(signedArea(outline.vertices));
}

/** Compute centroid of the panel outline polygon. */
export function computePanelCentroid(outline: PanelOutline): Vertex {
  const verts = outline.vertices;
  const n = verts.length;
  const A = signedArea(verts);
  if (Math.abs(A) < TOLERANCE * TOLERANCE) {
    // Degenerate — fallback to bounding box center
    const xs = verts.map(v => v.x);
    const ys = verts.map(v => v.y);
    return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
  }
  let cx = 0, cy = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const cross = verts[i].x * verts[j].y - verts[j].x * verts[i].y;
    cx += (verts[i].x + verts[j].x) * cross;
    cy += (verts[i].y + verts[j].y) * cross;
  }
  const factor = 1 / (6 * A);
  return { x: cx * factor, y: cy * factor };
}

// ── Diagonals ────────────────────────────────────────────────────────────────

/** Compute panel diagonals from outline. BL→top-right-corner, BR→top-left-corner. */
export function computeDiagonals(outline: PanelOutline): { d1: number; d2: number; match: boolean } {
  const { hL, hR } = outline;
  const W = outline.vertices[1]?.x ?? 0; // BR.x = width
  const d1 = Math.sqrt(W * W + hR * hR); // BL→TR
  const d2 = Math.sqrt(W * W + hL * hL); // BR→TL
  return { d1, d2, match: Math.abs(d1 - d2) < 0.125 };
}

// ── SVG path generation ─────────────────────────────────────────────────────

/**
 * Generate SVG path `d` attribute for the panel outline.
 * The SVG coordinate system has Y-down, so we flip Y.
 *
 * @param outline   Panel outline from computePanelOutline
 * @param scale     Pixels per inch
 * @param panLeft   SVG X of panel left edge
 * @param panBottom SVG Y of panel bottom edge
 */
export function makeSVGPath(
  outline: PanelOutline,
  scale: number,
  panLeft: number,
  panBottom: number,
): string {
  const verts = outline.vertices;
  if (verts.length < 3) return '';

  // SVG transform: sx(x) = panLeft + x * scale, sy(y) = panBottom - y * scale
  const sx = (x: number) => panLeft + x * scale;
  const sy = (y: number) => panBottom - y * scale;

  const parts: string[] = [`M ${sx(verts[0].x)} ${sy(verts[0].y)}`];
  for (let i = 1; i < verts.length; i++) {
    parts.push(`L ${sx(verts[i].x)} ${sy(verts[i].y)}`);
  }
  parts.push('Z');
  return parts.join(' ');
}

// ── THREE.js Shape generation ────────────────────────────────────────────────

/**
 * Create a THREE.Shape from the panel outline, with rectangular openings punched out.
 * Shape is in panel-space: (0,0) = bottom-left, Y-up.
 */
export function makeTHREEShape(
  outline: PanelOutline,
  openings: Array<{ cx: number; cy: number; w: number; h: number }>,
  roundBlockouts: Array<{ cx: number; cy: number; r: number }>,
  panelWidth: number,
  maxHeight: number,
): THREE.Shape {
  const shape = new THREE.Shape();
  const verts = outline.vertices;

  // Outer boundary — CCW
  shape.moveTo(verts[0].x, verts[0].y);
  for (let i = 1; i < verts.length; i++) {
    shape.lineTo(verts[i].x, verts[i].y);
  }
  shape.closePath();

  // Rectangular holes — CW winding (opposite to outer)
  for (const o of openings) {
    const l = Math.max(0, o.cx - o.w / 2);
    const r = Math.min(panelWidth, o.cx + o.w / 2);
    const b = Math.max(0, o.cy - o.h / 2);
    const t = Math.min(maxHeight, o.cy + o.h / 2);
    if (r <= l || t <= b) continue;
    const hole = new THREE.Path();
    hole.moveTo(l, b);
    hole.lineTo(l, t);
    hole.lineTo(r, t);
    hole.lineTo(r, b);
    hole.closePath();
    shape.holes.push(hole);
  }

  // Round holes — CW arc
  for (const o of roundBlockouts) {
    const hole = new THREE.Path();
    hole.absarc(o.cx, o.cy, o.r, 0, Math.PI * 2, true);
    shape.holes.push(hole);
  }

  return shape;
}

// ── DXF polyline generation ──────────────────────────────────────────────────

/**
 * Generate DXF LWPOLYLINE entity string for the panel outline.
 * Vertices in inches, Y-up, origin at (0,0) = panel bottom-left.
 */
export function makeDXFPolyline(outline: PanelOutline, layer: string, handleFn: () => string): string {
  const verts = outline.vertices;
  const n = verts.length;
  let s = `  0\nLWPOLYLINE\n  5\n${handleFn()}\n  8\n${layer}\n100\nAcDbEntity\n100\nAcDbPolyline\n`;
  s += ` 90\n${n}\n 70\n1\n`; // vertex count + closed flag
  for (const v of verts) {
    s += ` 10\n${v.x.toFixed(4)}\n 20\n${v.y.toFixed(4)}\n`;
  }
  return s;
}

// ── Helper: default outline params from MpVar fields ─────────────────────────

export interface MpVarLike {
  width:     number;
  height:    number;
  heightL?:  number;
  heightR?:  number;
  topAngleL: AngleSpec;
  topAngleR: AngleSpec;
  topRadiusL: number;
  topRadiusR: number;
}

/** Build TopAngleParams from an MpVar-shaped object. */
export function mpToOutlineParams(mp: MpVarLike): TopAngleParams {
  return {
    width:   mp.width,
    height:  mp.height,
    heightL: mp.heightL,
    heightR: mp.heightR,
    angleL:  mp.topAngleL ?? 'auto',
    angleR:  mp.topAngleR ?? 'auto',
    radiusL: mp.topRadiusL ?? 0,
    radiusR: mp.topRadiusR ?? 0,
  };
}

/** Convenience: compute outline directly from an MpVar-shaped object. */
export function outlineFromMp(mp: MpVarLike): PanelOutline {
  return computePanelOutline(mpToOutlineParams(mp));
}

/** Bounding box of the panel outline. */
export function outlineBBox(outline: PanelOutline): { minX: number; minY: number; maxX: number; maxY: number; w: number; h: number } {
  const xs = outline.vertices.map(v => v.x);
  const ys = outline.vertices.map(v => v.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}
